import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import TourCard from "./TourCard";

export interface TourStep {
  /** CSS selector or data attribute to anchor the card to */
  targetSelector: string;
  /** Separate selector for positioning the card (if different from spotlight target) */
  cardTargetSelector?: string;
  /** Fallback selector if primary target disappears (e.g., popover closed) */
  fallbackSelector?: string;
  /** Placement to use when falling back */
  fallbackPlacement?: "left" | "right" | "top" | "bottom";
  title: string;
  description: React.ReactNode;
  /** Where the card appears relative to the target */
  placement: "left" | "right" | "top" | "bottom";
  /** Offset from the target element in px */
  offset?: number;
  /** Vertical offset for the card position (positive = move down, negative = move up) */
  offsetY?: number;
  /** Action to run when this step becomes active (e.g., open a tab, scroll to element) */
  onEnter?: () => void;
  /** If set, auto-advance to next step when this event fires on this selector */
  advanceOn?: {
    /** CSS selector to observe */
    selector: string;
    /** DOM event to listen for (default: "change") */
    event: string;
  };
  /** Hide back/next buttons — step advances only via advanceOn */
  hideButtons?: boolean;
  /** Hide only the Next button — user must interact to advance (Skip still visible) */
  hideNext?: boolean;
  /** Disable the Next button (greyed out, not clickable). Can be a boolean or a function that evaluates dynamically. */
  disableNext?: boolean | (() => boolean);
  /** Hide the Skip/Back button on the left */
  hideSkip?: boolean;
  /** If returns true, this step is skipped automatically */
  skipIf?: () => boolean;
  /** Action to run when leaving this step (e.g., close a popover) */
  onExit?: () => void;
  /** Tool ID to highlight in the bottom toolbar during this step */
  highlightToolId?: string;
  /** Delay in ms before applying the highlight (default: 300) */
  highlightDelay?: number;
  /** When true, hide overlay+card when user starts creating (tool selected), show again after shape created */
  hideWhileCreating?: boolean;
  /** CSS selector to spotlight when in creating mode (e.g., right sidebar). If not set, overlay is fully removed during creating. */
  creatingSelector?: string;
}

interface TourProviderProps {
  steps: TourStep[];
  /** Whether the tour should be active */
  active: boolean;
  onComplete: () => void;
}

const TourProvider: React.FC<TourProviderProps> = ({ steps, active, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [activePlacementState, setActivePlacementState] = useState<string>("");
  const [spotlightRect, setSpotlightRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const prevStepRef = useRef<number>(0);
  const prevActiveRef = useRef<boolean>(false);
  const [, setTick] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [shapeCreatedWhileCreating, setShapeCreatedWhileCreating] = useState(false);
  const directionRef = useRef<"forward" | "backward">("forward");
  const suppressAdvanceRef = useRef(false);

  // Reset to step 0 when tour is re-activated (e.g., from help button)
  useEffect(() => {
    if (active && !prevActiveRef.current) {
      setCurrentStep(0);
      setPosition(null);
      prevStepRef.current = 0;
    }
    prevActiveRef.current = active;
  }, [active]);

  // Call onExit when leaving a step and reset state
  useEffect(() => {
    if (prevStepRef.current !== currentStep) {
      // Reset position so card doesn't flash at old location
      setPosition(null);
      const prevStep = steps[prevStepRef.current];
      if (prevStep?.onExit) prevStep.onExit();
      prevStepRef.current = currentStep;
      setIsCreating(false);
      setShapeCreatedWhileCreating(false);
    }
  }, [currentStep, steps]);

  const step = steps[currentStep];

  const updatePosition = useCallback(() => {
    if (!step || !active) return;

    let target = document.querySelector(step.targetSelector);
    let activePlacement = step.placement;

    // If primary target not found, try fallback (e.g., popover closed → use tool icon)
    if (!target && step.fallbackSelector) {
      target = document.querySelector(step.fallbackSelector);
      if (target && step.fallbackPlacement) {
        activePlacement = step.fallbackPlacement;
      }
    }

    if (!target) {
      // Retry on next frame if element isn't mounted yet
      rafRef.current = requestAnimationFrame(updatePosition);
      return;
    }

    const rect = target.getBoundingClientRect();
    // Use a separate element for card positioning if specified
    const cardTarget = step.cardTargetSelector ? document.querySelector(step.cardTargetSelector) : null;
    const cardRect = cardTarget ? cardTarget.getBoundingClientRect() : rect;
    const cardEl = cardRef.current;
    const cardWidth = cardEl?.offsetWidth || 300;
    const cardHeight = cardEl?.offsetHeight || 120;
    const offset = step.offset ?? 12;

    let top = 0;
    let left = 0;

    switch (activePlacement) {
      case "right":
        top = cardRect.top + cardRect.height / 2 - cardHeight / 2;
        left = rect.right + offset;
        break;
      case "left":
        top = cardRect.top + cardRect.height / 2 - cardHeight / 2;
        left = rect.left - cardWidth - offset;
        break;
      case "top":
        top = rect.top - cardHeight - offset;
        left = rect.left + rect.width / 2 - cardWidth / 2;
        break;
      case "bottom":
        top = rect.bottom + offset;
        left = rect.left + rect.width / 2 - cardWidth / 2;
        break;
    }

    // Apply vertical offset if specified
    if (step.offsetY) {
      top += step.offsetY;
    }

    setPosition({ top, left });
    setActivePlacementState(activePlacement);

    // Set spotlight rect
    const pad = 6;

    // When in creating mode with a creatingSelector and shape not yet created, spotlight the sidebar
    if (isCreating && step?.creatingSelector && !shapeCreatedWhileCreating) {
      const creatingTarget = document.querySelector(step.creatingSelector);
      if (creatingTarget) {
        const cRect = creatingTarget.getBoundingClientRect();
        setSpotlightRect({
          x: cRect.x,
          y: 0,
          w: cRect.width,
          h: window.innerHeight,
        });
        return;
      }
    }

    // When in creating mode without creatingSelector, no spotlight (full screen interactive)
    if (isCreating) {
      setSpotlightRect(null);
      return;
    }

    // Normal mode: spotlight around target, expanding for open dropdowns
    let spotX = rect.x - pad;
    let spotY = rect.y - pad;
    let spotRight = rect.right + pad;
    let spotBottom = rect.bottom + pad;

    // Expand spotlight to include any open MUI Popover that is near the target
    // This covers Select dropdowns, color pickers, and other popovers
    const muiPopovers = document.querySelectorAll(".MuiPopover-root .MuiPaper-root");
    muiPopovers.forEach((paper) => {
      const popRect = (paper as HTMLElement).getBoundingClientRect();
      // Only include popovers that overlap or are adjacent to the target area (within 50px)
      const isNearTarget =
        popRect.right > rect.x - 50 &&
        popRect.x < rect.right + 50 &&
        popRect.bottom > rect.y - 50 &&
        popRect.y < rect.bottom + 200;
      if (isNearTarget && popRect.width > 0 && popRect.height > 0) {
        spotX = Math.min(spotX, popRect.x - pad);
        spotY = Math.min(spotY, popRect.y - pad);
        spotRight = Math.max(spotRight, popRect.right + pad);
        spotBottom = Math.max(spotBottom, popRect.bottom + pad);
      }
    });

    setSpotlightRect({
      x: spotX,
      y: spotY,
      w: spotRight - spotX,
      h: spotBottom - spotY,
    });
  }, [step, active, isCreating, shapeCreatedWhileCreating]);

  // Skip steps that should be skipped (e.g., address box when not visible)
  useEffect(() => {
    if (!active || !step?.skipIf) return;
    if (step.skipIf()) {
      // Skip in the same direction the user was navigating
      const dir = directionRef.current;
      setCurrentStep((prev) => {
        if (dir === "backward" && prev > 0) return prev - 1;
        if (prev < steps.length - 1) return prev + 1;
        return prev - 1;
      });
    }
  }, [active, currentStep, step, steps.length]);

  // Run onEnter and highlight tool when step changes
  useEffect(() => {
    if (!active) return;
    // Don't run if step should be skipped
    if (step?.skipIf?.()) return;

    if (step?.onEnter) step.onEnter();

    // Highlight tool icon in toolbar — delay to run after onEnter (which may reset hover state)
    const highlightMs = step?.highlightDelay ?? 300;
    setTimeout(() => {
      if (step?.highlightToolId) {
        window.dispatchEvent(new CustomEvent("tour:highlight-tool", { detail: { toolId: step.highlightToolId } }));
      } else {
        window.dispatchEvent(new CustomEvent("tour:highlight-tool", { detail: { toolId: null } }));
      }
    }, highlightMs);
  }, [active, currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active) return;

    // If step has onEnter, delay initial positioning to let the DOM update after the action
    const initialDelay = step?.onEnter ? 300 : 0;
    const delayTimer = setTimeout(() => {
      updatePosition();
    }, initialDelay);

    // Also update on scroll/resize
    const handleUpdate = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updatePosition);
    };

    window.addEventListener("resize", handleUpdate);
    window.addEventListener("scroll", handleUpdate, true);

    // Poll position until element is found (covers async tab opens, animations, etc.)
    const interval = setInterval(() => {
      updatePosition();
      setTick((t) => t + 1); // Force re-render to re-evaluate disableNext
    }, 150);
    // Keep polling longer for steps that open panels/popovers
    const pollDuration = step?.onEnter ? 5000 : 3000;
    const timeout = setTimeout(() => clearInterval(interval), pollDuration);

    return () => {
      clearTimeout(delayTimer);
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", handleUpdate, true);
      clearInterval(interval);
      clearTimeout(timeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, currentStep, updatePosition]);

  // Listen for "creating start" — user selected a tool from the popover, hide overlay to let them draw
  useEffect(() => {
    if (!active || !step?.hideWhileCreating) return;

    const handleCreatingStart = () => {
      setIsCreating(true);
      setShapeCreatedWhileCreating(false);
    };

    // When a shape is created during creating mode with creatingSelector,
    // switch to fullscreen overlay so user can interact with the canvas
    const handleShapeCreatedDuringCreating = () => {
      if (isCreating && step?.creatingSelector) {
        setShapeCreatedWhileCreating(true);
      }
    };

    window.addEventListener("tour:creating-start", handleCreatingStart);
    document.body.addEventListener("tour:shape-created", handleShapeCreatedDuringCreating);
    return () => {
      window.removeEventListener("tour:creating-start", handleCreatingStart);
      document.body.removeEventListener("tour:shape-created", handleShapeCreatedDuringCreating);
    };
  }, [active, currentStep, step, isCreating]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for advanceOn events to auto-advance the step
  useEffect(() => {
    if (!active || !step?.advanceOn) return;

    const { selector, event } = step.advanceOn;
    let cleanupFn: (() => void) | null = null;

    const setupListener = () => {
      const target = document.querySelector(selector);
      if (!target) return;

      const handler = () => {
        // Skip if suppressed (manual Next/Back just fired, avoid double-advance from onExit side effects)
        if (suppressAdvanceRef.current) return;
        // If we were in "creating" mode, restore overlay first
        if (isCreating) setIsCreating(false);
        setPosition(null);
        setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
      };
      target.addEventListener(event, handler);
      cleanupFn = () => target.removeEventListener(event, handler);
    };

    // Try immediately, then poll in case element isn't ready
    setupListener();
    const poll = setInterval(() => {
      if (!cleanupFn) setupListener();
    }, 200);
    const pollTimeout = setTimeout(() => clearInterval(poll), 3000);

    return () => {
      cleanupFn?.();
      clearInterval(poll);
      clearTimeout(pollTimeout);
    };
  }, [active, currentStep, steps.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      directionRef.current = "forward";
      suppressAdvanceRef.current = true;
      setTimeout(() => {
        suppressAdvanceRef.current = false;
      }, 500);
      setPosition(null);
      setCurrentStep((prev) => prev + 1);
    } else {
      // Clear tool highlight when tour ends
      window.dispatchEvent(new CustomEvent("tour:highlight-tool", { detail: { toolId: null } }));
      onComplete();
    }
  }, [currentStep, steps.length, onComplete]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      directionRef.current = "backward";
      suppressAdvanceRef.current = true;
      setTimeout(() => {
        suppressAdvanceRef.current = false;
      }, 500);
      setPosition(null);
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    // Clear tool highlight when tour ends
    window.dispatchEvent(new CustomEvent("tour:highlight-tool", { detail: { toolId: null } }));
    onComplete();
  }, [onComplete]);

  if (!active || !step || !position) return null;

  // Determine arrow direction (opposite of active placement)
  const currentPlacement = activePlacementState || step?.placement || "right";
  const arrowPosition =
    currentPlacement === "right"
      ? "left"
      : currentPlacement === "left"
      ? "right"
      : currentPlacement === "bottom"
      ? "top"
      : "bottom";

  const overlay = (
    <div style={{ position: "fixed", inset: 0, zIndex: 99998, pointerEvents: "none" }}>
      <div
        ref={cardRef}
        style={{
          position: "absolute",
          top: position.top,
          left: position.left,
          pointerEvents: "auto",
          zIndex: 99999,
        }}
      >
        <TourCard
          title={step.title}
          description={step.description}
          currentStep={currentStep}
          totalSteps={steps.length}
          onBack={handleBack}
          onNext={handleNext}
          onSkip={handleSkip}
          arrowPosition={arrowPosition}
          hideButtons={step.hideButtons}
          hideNext={step.hideNext}
          disableNext={typeof step.disableNext === "function" ? step.disableNext() : step.disableNext}
          hideSkip={step.hideSkip}
        />
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
};

export default TourProvider;
